import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { matchPath, NavLink, Outlet, useLocation } from 'react-router-dom';

import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/_shared/components/ui/empty';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { useIsSiblingWorkspace } from '@/_shared/hooks/useSiblingStatus';
import { cn } from '@/_shared/lib/utils';

/** The classes of every tab link. */
const TAB_CLASS = '-mb-px shrink-0 border-b-2 px-0.5 py-3 text-sm font-medium whitespace-nowrap transition-colors';

interface TabPath {
  path: string;
  label: string;
  end: boolean;
}

const TABS: TabPath[] = [
  { path: '/settings', label: 'General', end: true },
  // Sibling workspaces only (ACT2-1124): the members and invites, with each member's RudderStack role and
  // permissions. Any other workspace keeps its members and invites on General.
  { path: '/settings/users', label: 'Users', end: false },
  // The AI controls and GitHub: what the agent may read and keep.
  { path: '/settings/context', label: 'Context', end: false },
  { path: '/settings/warehouses', label: 'Warehouses', end: false },
  // The only settings surface that is an unbounded COLLECTION — accounts grow with every destination
  // a customer adds — so it is the one that needed a list page's search, filters and views.
  { path: '/settings/accounts', label: 'Accounts', end: false },
  { path: '/settings/integrations', label: 'Integrations', end: false },
  { path: '/settings/usage', label: 'Usage', end: false },
];

/**
 * The list tabs. Their page fills the content area: its table keeps its header in view and scrolls its own rows,
 * as on the Audiences and Activations pages (`<Table fillHeight>`), and its card view scrolls its own grid.
 */
const FILL_HEIGHT_PATHS = ['/settings/warehouses', '/settings/accounts'];

export function SettingsShell(): React.ReactElement {
  const { wp, isAdmin, isUsage, isRsAccounts } = useWorkspace();
  const orpc = useOrpc();
  const { data } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));
  const isSiblingWorkspace = useIsSiblingWorkspace() === true;
  const { pathname } = useLocation();
  const fillsHeight = FILL_HEIGHT_PATHS.some((path) => matchPath({ path: wp(path), end: false }, pathname) !== null);
  // `isRsAccounts`, not `isAudiences`: it is `isAudiences && rsAccountsEnabled`, which mirrors the
  // surface's own gate chain (`requireAudiencesFlag` then `requireRsAccountsFlag`). Both halves hide
  // the tab for a real reason — a workspace in `AUDIENCES_DISABLED_WORKSPACE_SLUGS`, and a challenge
  // workspace, which performs zero RudderStack writes.
  const tabs = TABS.filter(
    (t) =>
      (t.path !== '/settings/usage' || isUsage) &&
      (t.path !== '/settings/accounts' || isRsAccounts) &&
      (t.path !== '/settings/users' || isSiblingWorkspace),
  );

  // In a challenge workspace, participants (members) get a locked, pre-configured environment:
  // settings are managed by the booth team. Owners/admins keep full access. Non-challenge
  // workspaces are unaffected (challengeMode is false), so this is a no-op everywhere else.
  if ((data?.challengeMode ?? false) && !isAdmin) {
    return (
      <div className="flex flex-1 items-center justify-center p-6">
        <Empty className="max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Lock />
            </EmptyMedia>
            <EmptyTitle>You're all set for the challenge</EmptyTitle>
            <EmptyDescription>
              Your workspace is ready to go. The RudderStack team has already configured the warehouse, sources, and
              data for you, so there's nothing to set up here. Head back and start exploring your data.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-3 md:px-8">
        <h1 className="text-2xl font-semibold tracking-[-0.03em] text-foreground">Workspace Settings</h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          Manage data connections, integrations, and workspace membership from one shared control plane.
        </p>

        <nav
          aria-label="Settings sections"
          className="-mx-6 mt-6 scrollbar-none flex items-center gap-6 overflow-x-auto border-b border-border px-6 md:-mx-8 md:gap-8 md:px-8"
        >
          {tabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={wp(tab.path)}
              end={tab.end}
              className={({ isActive }) =>
                cn(
                  TAB_CLASS,
                  isActive
                    ? 'border-primary font-medium text-primary'
                    : 'border-transparent text-foreground hover:border-grey-200 hover:text-foreground',
                )
              }
            >
              {tab.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {/* A list tab is a flex column of a fixed height, so its table can take the rest of it. It still scrolls when
          what is above the table does not fit. */}
      <div
        className={cn('flex-1 overflow-y-auto px-6 pt-3 pb-6 md:px-8 md:pb-8', fillsHeight && 'flex min-h-0 flex-col')}
      >
        <div className={cn('max-w-3xl', fillsHeight && 'flex min-h-0 w-full flex-1 flex-col')}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}
